import { SidebarTrigger, useSidebar } from '@/components/ui/sidebar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { FileText, Download, Edit, Share2, Linkedin } from 'lucide-react';
import SEO from '@/components/SEO';

const mockResume = {
  name: 'John Veteran',
  targetRole: 'Frontend Developer',
  summary: 'Transitioning service member with 8 years of experience in logistics and operations, now pursuing a career in frontend development. Strong problem-solving skills, leadership experience, and proven ability to learn complex technical systems quickly.',
  technicalSkills: [
    'HTML/CSS', 'JavaScript', 'React', 'TypeScript', 'Git/GitHub', 'REST APIs', 'Responsive Design'
  ],
  experience: [
    {
      title: 'Logistics Specialist',
      organization: 'U.S. Army',
      period: '2016-2024',
      bullets: [
        'Managed supply chain operations for 500+ personnel, ensuring 99.8% inventory accuracy',
        'Developed automated tracking system that reduced processing time by 40%',
        'Led team of 12 specialists in high-pressure environments requiring rapid decision-making',
        'Trained 50+ personnel on inventory management systems and procedures'
      ]
    }
  ],
  projects: [
    {
      title: 'Task Manager Application',
      tech: 'React, TypeScript, CSS',
      description: 'Built full-featured task management app with priority sorting, due dates, and category filtering',
      link: 'github.com/user/task-manager'
    },
    {
      title: 'Portfolio Website',
      tech: 'HTML, CSS, JavaScript',
      description: 'Responsive personal portfolio showcasing projects and technical skills',
      link: 'myportfolio.com'
    }
  ],
  education: [
    'freeCodeCamp Responsive Web Design Certification',
    'JavaScript Algorithms and Data Structures (in progress)',
    'U.S. Army Advanced Leadership Course'
  ]
};

export default function ResumeBrief() {
  const { isMobile } = useSidebar();

  return (
    <div className="flex flex-col flex-1 overflow-y-auto p-4 md:p-6 lg:p-8 bg-background text-foreground space-y-6 pb-16">
      <SEO title="Resume & Brief - VeteranOS" description="Generate ATS-optimized resume and transition brief" />
      
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center border-b border-border pb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-primary flex items-center gap-2">
            <FileText className="h-7 w-7" />
            Resume & Transition Brief
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            ATS-optimized resume and artifacts for your transition
          </p>
        </div>
        <div className="flex items-center gap-2 mt-4 md:mt-0">
          <Button variant="outline" size="sm">
            <Share2 className="h-4 w-4 mr-2" />
            Share
          </Button>
          <Button size="sm">
            <Download className="h-4 w-4 mr-2" />
            Download PDF
          </Button>
          {isMobile && <SidebarTrigger />}
        </div>
      </div>

      {/* Tabs for Resume/LinkedIn/Brief */}
      <Tabs defaultValue="resume" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="resume">Resume</TabsTrigger>
          <TabsTrigger value="linkedin">LinkedIn</TabsTrigger>
          <TabsTrigger value="brief">Transition Brief</TabsTrigger>
        </TabsList>

        <TabsContent value="resume" className="mt-6">
          <Card accent>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-2xl">{mockResume.name}</CardTitle>
                  <CardDescription className="text-base mt-1">
                    {mockResume.targetRole}
                  </CardDescription>
                </div>
                <Button variant="outline" size="sm">
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Summary */}
              <div>
                <h3 className="font-semibold text-lg text-primary mb-2">Professional Summary</h3>
                <p className="text-sm text-foreground leading-relaxed">
                  {mockResume.summary}
                </p>
              </div>

              <Separator />

              {/* Technical Skills */}
              <div>
                <h3 className="font-semibold text-lg text-primary mb-3">Technical Skills</h3>
                <div className="flex flex-wrap gap-2">
                  {mockResume.technicalSkills.map((skill, idx) => (
                    <Badge key={idx} variant="branch">
                      {skill}
                    </Badge>
                  ))}
                </div>
              </div>

              <Separator />

              {/* Experience */}
              <div>
                <h3 className="font-semibold text-lg text-primary mb-3">Experience</h3>
                {mockResume.experience.map((exp, idx) => (
                  <div key={idx} className="space-y-2">
                    <div>
                      <h4 className="font-semibold text-foreground">{exp.title}</h4>
                      <p className="text-sm text-muted-foreground">
                        {exp.organization} | {exp.period}
                      </p>
                    </div>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      {exp.bullets.map((bullet, bIdx) => (
                        <li key={bIdx} className="text-sm text-foreground">
                          {bullet}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              <Separator />

              {/* Projects */}
              <div>
                <h3 className="font-semibold text-lg text-primary mb-3">Projects</h3>
                <div className="space-y-4">
                  {mockResume.projects.map((project, idx) => (
                    <div key={idx}>
                      <h4 className="font-semibold text-foreground">{project.title}</h4>
                      <p className="text-xs text-muted-foreground mb-1">{project.tech}</p>
                      <p className="text-sm text-foreground">{project.description}</p>
                      <a 
                        href={`https://${project.link}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-xs text-primary hover:underline"
                      >
                        {project.link}
                      </a>
                    </div>
                  ))}
                </div>
              </div>

              <Separator />

              {/* Education */}
              <div>
                <h3 className="font-semibold text-lg text-primary mb-3">Education & Certifications</h3>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  {mockResume.education.map((item, idx) => (
                    <li key={idx} className="text-sm text-foreground">
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="linkedin" className="mt-6">
          <Card accent>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Linkedin className="h-5 w-5 text-primary" />
                LinkedIn Profile Generator
              </CardTitle>
              <CardDescription>
                Optimized content for your LinkedIn profile
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="font-semibold text-sm text-muted-foreground mb-2">Headline</h3>
                <p className="text-foreground p-3 bg-muted/30 rounded-lg">
                  Transitioning Army Veteran | Aspiring Frontend Developer | React & JavaScript | Building Modern Web Applications
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-sm text-muted-foreground mb-2">About</h3>
                <p className="text-sm text-foreground p-3 bg-muted/30 rounded-lg leading-relaxed">
                  {mockResume.summary}
                  <br /><br />
                  Currently building projects in React and TypeScript while completing my transition from military service. I bring strong leadership, problem-solving skills, and a commitment to excellence from 8 years of service.
                </p>
              </div>
              <Button variant="outline" className="w-full">
                <Linkedin className="h-4 w-4 mr-2" />
                Copy to LinkedIn
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="brief" className="mt-6">
          <Card accent>
            <CardHeader>
              <CardTitle>Transition Brief</CardTitle>
              <CardDescription>
                One-page executive summary for sharing with mentors and recruiters
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-center p-6 bg-muted/30 rounded-lg">
                <FileText className="h-16 w-16 mx-auto text-primary mb-4" />
                <h3 className="font-semibold text-lg mb-2">Professional Transition Brief</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Download a polished one-page PDF summarizing your background, target role, skills, and next milestones
                </p>
                <Button>
                  <Download className="h-4 w-4 mr-2" />
                  Generate Transition Brief PDF
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
